/**
 * Shared Axios client for all Compass API calls.
 *
 * All domain API modules import `client` for HTTP requests. Centralizing
 * the HTTP client here gives us one place to add auth interceptors, error
 * normalization, request logging, and timeout policy as the app grows.
 *
 * Auth: attaches `Authorization: Bearer <token>` from the auth store on
 * every request. On 401 response, clears the auth store and forces a
 * page-level redirect to /login.
 */
import axios, { AxiosError, AxiosRequestConfig } from 'axios'
import { useAccountStore } from '@/stores/accountStore'
import { useAuthStore } from '@/stores/authStore'
import { useTenantStore } from '@/stores/tenantStore'

export const API_BASE = '/api/v1'

export const client = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
  timeout: 30000,
})

// ---------------------------------------------------------------------------
// Request interceptor — attach Compass-issued bearer token if we have one
// ---------------------------------------------------------------------------

client.interceptors.request.use((config) => {
  const token = useAuthStore.getState().token
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  // Multi-tenant: attach X-Compass-Tenant when scope is known.
  const tenant = useTenantStore.getState().currentTenant
  if (tenant?.id) {
    config.headers['X-Compass-Tenant'] = tenant.id
  }
  // Account scope travels here and ONLY here. It used to be a per-call query
  // param in five different spellings (alias / account / account_alias /
  // scope / a body field), each optional and each meaning "every account"
  // when omitted — so forgetting one was silent and looked like it worked.
  // The selected alias, or the literal 'all', on every request.
  const alias = useAccountStore.getState().activeAlias
  config.headers['X-Compass-Account'] = alias ?? 'all'
  return config
})

// ---------------------------------------------------------------------------
// Response interceptor — normalize errors + handle 401
// ---------------------------------------------------------------------------

export interface ApiError {
  status: number
  message: string
  detail?: unknown
}

client.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      // Token rejected by Compass auth. Clear local state.
      useAuthStore.getState().clearAuth()
      // If we're not already on the login page, force a navigation.
      if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
        const here = window.location.pathname + window.location.search
        window.location.href = `/login?next=${encodeURIComponent(here)}`
      }
    }
    const apiError: ApiError = {
      status: error.response?.status ?? 0,
      message: error.message,
      detail: error.response?.data,
    }
    return Promise.reject(apiError)
  },
)

/**
 * Best human-readable message from a rejected API call. Backend error responses
 * carry the real reason in the body (`{ error: ... }`, or `{ detail: ... }`),
 * which axios hides behind a generic "Request failed with status code 5xx".
 * Use this in catch blocks instead of `err.message` so the user sees the cause
 * (e.g. an IB "no security definition" rejection on a bad contract).
 */
/**
 * The backend's error `category` for a rejected call, when it sent one.
 *
 * Only one value currently changes behaviour: `order_rejected` means the
 * broker has already refused this exact order — a price off the tick grid, an
 * unknown contract — so retrying it unchanged is guaranteed to fail the same
 * way. Anything else (a timeout, a disconnected gateway) says nothing about
 * whether the order itself is good.
 */
export function apiErrorCategory(err: unknown): string | null {
  const detail = (err as Partial<ApiError> | undefined)?.detail
  if (detail && typeof detail === 'object') {
    const c = (detail as Record<string, unknown>).category
    if (typeof c === 'string' && c) return c
  }
  return null
}

/**
 * Categories that are a verdict on the order itself: the broker refused it
 * (`order_rejected` — off-tick price, bad size) or refused the instrument
 * (`instrument_rejected` — unqualifiable legs, no permissions, halted).
 * Either way, resending the same order gets the same answer.
 *
 * Deliberately excludes `transient` and `gateway_disconnected`, which are
 * about reaching IB rather than about the order, and `permanent` /
 * `invalid_request`, which are Compass-side faults the user cannot act on by
 * editing the ticket.
 */
const ORDER_REJECTION_CATEGORIES = new Set(['order_rejected', 'instrument_rejected'])

/** True when the broker refused this order outright — see apiErrorCategory. */
export function isOrderRejection(err: unknown): boolean {
  const c = apiErrorCategory(err)
  return c != null && ORDER_REJECTION_CATEGORIES.has(c)
}

export function apiErrorMessage(err: unknown, fallback = 'Request failed'): string {
  const e = err as Partial<ApiError> | undefined
  const detail = e?.detail
  if (detail && typeof detail === 'object') {
    const d = detail as Record<string, unknown>
    if (typeof d.error === 'string' && d.error) return d.error
    if (typeof d.detail === 'string' && d.detail) return d.detail
  } else if (typeof detail === 'string' && detail) {
    return detail
  }
  return e?.message || fallback
}

// ---------------------------------------------------------------------------
// Generic HTTP helpers
// ---------------------------------------------------------------------------

export async function apiGet<T>(path: string, config?: AxiosRequestConfig): Promise<T> {
  const r = await client.get<T>(path, config)
  return r.data
}
export async function apiPost<T>(path: string, body?: unknown, config?: AxiosRequestConfig): Promise<T> {
  const r = await client.post<T>(path, body, config)
  return r.data
}
export async function apiPut<T>(path: string, body?: unknown, config?: AxiosRequestConfig): Promise<T> {
  const r = await client.put<T>(path, body, config)
  return r.data
}
export async function apiPatch<T>(path: string, body?: unknown, config?: AxiosRequestConfig): Promise<T> {
  const r = await client.patch<T>(path, body, config)
  return r.data
}
export async function apiDelete<T>(path: string, config?: AxiosRequestConfig): Promise<T> {
  const r = await client.delete<T>(path, config)
  return r.data
}
